import { RequirementModal } from '../Modals/RequirementModal';
import { Modal, rem, CheckIcon } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { useParams } from 'react-router-dom';
import {
  useClientIPPFormContext,
  useRequirementFormContext,
} from '../ClientIppSection';
import useSWR, { useSWRConfig } from 'swr';
import {
  type Requirement,
  type Requirements,
} from '@/schemas/API/GET/client/carePlan/requirement/response';
import { deleteFetcher, getFetcher } from '@/api/fetchers';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { displayError } from '@/utils/error/errorHandler';
import { DropDownList } from '@/components/DropDownList/DropDownList';
import { IconPencil, IconTrash } from '@tabler/icons-react';

export const RequirementsSettingsCard: React.FC = () => {
  const { clientId } = useParams<{
    clientId: string;
  }>();
  const careplanForm = useClientIPPFormContext();
  const requirementForm = useRequirementFormContext();
  const { data: requirements } = useSWR<Requirements>(
    `clients/${clientId}/careplans/${careplanForm.values.id}/requirements`,
    getFetcher
  );

  const [selectedRequirementId, setSelectedRequirementId] = useState<
    number | null
  >(null);

  const { mutate } = useSWRConfig();
  const [modalOpened, { open, close }] = useDisclosure(false);

  function showConfirmModal(deleteId: number): void {
    modals.openConfirmModal({
      title: 'Smazat požadavek',
      centered: true,
      children: (
        <div>Určitě chcete požadavek smazat? Tato akce je nevratná.</div>
      ),
      labels: { confirm: 'Smazat', cancel: 'Zrušit' },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        void deleteRequirement(deleteId);
      },
    });
  }

  const deleteRequirement = async (deleteId: number): Promise<void> => {
    try {
      await deleteFetcher(
        `/clients/${clientId}/careplans/${careplanForm.values.id}/requirements/${deleteId}`
      );

      notifications.show({
        message: 'Požadavek byl smazán.',
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
      mutate(
        `clients/${clientId}/careplans/${careplanForm.values.id}/requirements`
      );
    } catch (err) {
      displayError(err);
    }
  };

  return (
    <>
      <div className="border rounded-card p-4">
        <DropDownList
          title="Nastavení požadavků"
          list={requirements?.content}
          createNewItem={() => {
            setSelectedRequirementId(null);
            open();
          }}
          createNewItemButtonText="Nový požadavek"
          itemToolBar={[
            {
              index: 0,
              function: (item: Requirement) => {
                setSelectedRequirementId(item.id);
                open();
              },
              icon: <IconPencil data-testid={`pencil-icon`}/>,
              onHoverColor: 'blue',
            },
            {
              index: 1,
              function: (item: Requirement) => {
                showConfirmModal(item.id);
              },
              icon: <IconTrash data-testid={`delete-icon`} />,
            },
          ]}
        />
      </div>

      <Modal
        opened={modalOpened}
        onClose={() => {
          close();
          requirementForm.reset();
        }}
        centered
        withCloseButton={false}
        size="lg"
      >
        <RequirementModal
          requirementId={selectedRequirementId}
          closeModal={close}
        />
      </Modal>
    </>
  );
};
